import { api, canWrite, isOwner, send } from "../api";
import { adminOrders, ordersTable, planName, runningPanel } from "../parts";
import type { Detail } from "../types";
import { actionButton, ago, chart, confirmButton, date, dateTime, dayRange, days, detailBox, field, figures, flash, form, geoCell, h, hours, input, link, nodeLink, place, select, show, size, statusChip, table, td, textarea, th, who } from "../ui";

export async function userPage() {
  const id = new URLSearchParams(location.search).get("id") ?? "";
  if (!id) return show("User", "No user given.", link("/users", "← All users"));
  const d = await api<Detail>(`/v1/admin/accounts/${encodeURIComponent(id)}?days=${days()}`);
  if (!d) return;
  const write = canWrite();
  const names = new Map(d.devices.map((x) => [x.id, x.name]));
  const hostUse = d.usage.filter((u) => u.kind === "host");
  const t = hostUse.reduce(
    (a, u) => ({ i: a.i + u.relay_in, o: a.o + u.relay_out, s: a.s + u.sessions, g: a.g + u.guest_sessions, ss: a.ss + u.session_seconds, on: a.on + u.online_seconds }),
    { i: 0, o: 0, s: 0, g: 0, ss: 0, on: 0 },
  );
  const computers = d.devices.filter((x) => x.kind === "host" && !x.revoked_at);
  const dl = dayRange(d.from, d.to);
  const perDay = (k: "relay_in" | "relay_out") => {
    const m = new Map<string, number>();
    for (const u of hostUse) m.set(u.day, (m.get(u.day) ?? 0) + u[k]);
    return m;
  };
  const ord = await adminOrders({ q: d.account.id, limit: 50 });
  const now = Date.now() / 1000;
  const aid = encodeURIComponent(d.account.id);

  // Notes: free text kept on the account, saved on demand.
  const notes = textarea({ "aria-label": "Notes", maxlength: "2000", placeholder: "Anything the staff should know about this account." }, d.account.notes ?? "");
  const notesForm = write
    ? form(
        "",
        async () => {
          await send("PUT", `/v1/admin/accounts/${aid}/notes`, { notes: notes.value });
          flash("Notes saved.");
        },
        notes,
        h("div", { class: "actions" }, h("button", { class: "btn small primary", type: "submit" }, "Save notes")),
      )
    : h("p", { class: "muted" }, d.account.notes || "No notes.");

  // Grant a plan by hand: a zero-amount order with status "manual".
  const plan = select({ "aria-label": "Plan" }, [["basic", "Basic"], ["pro", "Pro"], ["team", "Team"]], "pro");
  const period = select({ "aria-label": "Period" }, [["month", "1 month"], ["year", "1 year"]], "month");
  const seats = input({ type: "number", min: "1", max: "500", value: "5", "aria-label": "Seats" });
  const note = input({ placeholder: "Why (kept in the log)", maxlength: "200" });
  const seatsField = field("Seats (Team)", seats);
  seatsField.hidden = true;
  plan.addEventListener("change", () => (seatsField.hidden = plan.value !== "team"));
  const grantForm = form(
    "fields",
    async () => {
      const r = await send<{ order_id: string; term: { ends_at: number } }>("POST", `/v1/admin/accounts/${aid}/grant`, {
        plan: plan.value,
        period: period.value,
        ...(plan.value === "team" ? { seats: Number(seats.value) || 1 } : {}),
        note: note.value.trim(),
      });
      await userPage();
      flash(`Granted ${planName(plan.value)} for a ${period.value} (order ${r.order_id}, until ${date(r.term.ends_at)}).`);
    },
    field("Plan", plan),
    field("Period", period),
    seatsField,
    field("Note", note),
    h("div", null, h("button", { class: "btn small primary", type: "submit" }, "Grant")),
  );

  // Owners: delete Luwu's data for the account, confirmed twice (the second time by typing the id).
  const typed = input({ placeholder: d.account.id, "aria-label": "Type the account id to confirm", autocomplete: "off" });
  const deleteBox = h("div", { class: "twice" });
  const armDelete = confirmButton("Delete this account's data", "Delete: devices, shares, prompts, usage. Sure?", async () => {
    deleteBox.replaceChildren(
      h("p", { class: "muted" }, "Type the account id to confirm. Orders stay for the books; the account at accounts.luwu.dev stays too."),
      typed,
      actionButton(
        "Delete for good",
        async () => {
          if (typed.value.trim() !== d.account.id) throw new Error("The id doesn't match. Nothing was deleted.");
          await send("DELETE", `/v1/admin/accounts/${aid}`);
          show("Deleted", `Luwu's data for ${d.account.id} is gone.`, link("/users", "← All users"));
        },
        "btn small danger armed",
      ),
    );
  });

  show(
    who(d.account),
    [d.account.email, `id ${d.account.id}`, `joined ${date(d.account.first_seen)}`, `active ${ago(d.account.last_seen)}`, d.account.last_ip ? `from ${d.account.last_ip}, ${place(d.account)}` : null].filter(Boolean).join(" · "),
    h(
      "div",
      { class: "actions" },
      link("/users", "← All users"),
      write
        ? confirmButton("Block this account", `Block ${who(d.account)}: revoke all devices`, async () => {
            const r = await send<{ effect: string }>("POST", "/v1/admin/blocks", { kind: "account", value: d.account.id, reason: "" });
            await userPage();
            flash(r.effect);
          })
        : null,
      isOwner() ? armDelete : null,
    ),
    isOwner() ? deleteBox : null,
    figures([
      ["Relay traffic", size(t.i + t.o), `${size(t.o)} to · ${size(t.i)} from computers`],
      ["Computers", String(computers.length), `${computers.filter((x) => x.online).length} online now`],
      ["Sessions", String(t.s), `${hours(t.ss)} · ${t.g} by friends`],
      ["Online", hours(t.on), `${new Set(hostUse.map((u) => u.day)).size} active days`],
      ["Prompts", String(d.prompts)],
      ["Plan", d.terms.find((x) => x.starts_at <= now && x.ends_at > now) ? planName(d.terms.find((x) => x.starts_at <= now && x.ends_at > now)!.plan) : "Free", d.teams.filter((x) => !x.deleted_at).map((x) => `${x.name} (${x.role})`).join(", ") || "no team"],
    ]),
    h("h2", null, "Notes"),
    notesForm,
    h("h2", null, "Plan"),
    table(
      [th("Plan"), th("Seats", "num"), th("From", "num"), th("Until", "num"), th("Order"), th("Team")],
      d.terms.map((x) => h("tr", null, h("td", null, planName(x.plan), x.starts_at <= now && x.ends_at > now ? h("span", { class: "muted" }, " · current") : null), td(String(x.seats), "num"), td(date(x.starts_at), "num"), td(date(x.ends_at), "num"), h("td", null, x.order_id ? link(`/order?id=${encodeURIComponent(x.order_id)}`, x.order_id) : "—"), td(x.org_id ? (d.teams.find((tm) => tm.id === x.org_id)?.name ?? x.org_id) : "—"))),
      "No paid or granted term. The account is on Free.",
    ),
    write ? h("h3", null, "Grant a plan") : null,
    write ? h("p", { class: "muted small" }, "A term with no payment, logged as a manual order. It stacks on a current term of the same plan; a Team plan goes to a team this account owns.") : null,
    write ? grantForm : null,
    h("h2", null, "Relay traffic per day"),
    chart(dl, [
      { name: "out", label: "to computers", values: perDay("relay_out") },
      { name: "in", label: "from computers", values: perDay("relay_in") },
    ], size),
    h("h2", null, "By node"),
    table(
      [th("Node"), th("Received", "num"), th("Sent", "num")],
      d.nodes.map((x) => h("tr", null, h("td", null, nodeLink(x.relay)), td(size(x.relay_in), "num"), td(size(x.relay_out), "num"))),
      "No per-node traffic in this period.",
    ),
    h("h2", null, "Devices"),
    table(
      [th("Name"), th("Kind"), th("Platform"), th("Version"), th("State"), th("Relays"), th("Location"), th("Added", "num"), th("Last seen", "num"), th("", "num")],
      d.devices.map((x) => {
        const state = x.revoked_at ? "removed" : x.kind === "host" ? (x.online ? "online" : "offline") : "active";
        const actions = h("td", { class: "num" });
        if (!x.revoked_at) {
          if (x.kind === "host") {
            const showBtn = h("button", { class: "btn small plain", type: "button" }, "Running") as HTMLButtonElement;
            showBtn.addEventListener("click", async () => {
              showBtn.disabled = true;
              try {
                const panel = await runningPanel(x.id, write);
                const row = showBtn.closest("tr")!;
                row.after(h("tr", null, h("td", { colspan: "10" }, panel)));
              } catch (e) {
                flash((e as Error).message, true);
                showBtn.disabled = false;
              }
            });
            actions.append(showBtn, " ");
          }
          if (write) {
            actions.append(
              confirmButton("Revoke", "Revoke: they can add it again", async () => {
                await send("POST", `/v1/admin/devices/${encodeURIComponent(x.id)}/revoke`, { reason: "" });
                await userPage();
                flash(`${x.name || x.id} revoked. They can pair or register it again.`);
              }, "btn small plain"),
              " ",
              confirmButton("Block", "Block: revoke and refuse it", async () => {
                const r = await send<{ effect: string }>("POST", "/v1/admin/blocks", { kind: "device", value: x.id, reason: "" });
                await userPage();
                flash(r.effect);
              }),
            );
          }
        }
        return h("tr", null, h("td", null, x.name || x.id, h("span", { class: "muted block mono" }, x.id)), td(x.kind === "host" ? "computer" : "phone/terminal"), td(x.platform || "—"), td(x.app_version || "—"), h("td", null, statusChip(state)), td(x.relays.join(", ") || "—", "mono"), geoCell(x), td(date(x.created_at), "num"), td(ago(x.last_seen_at), "num"), actions);
      }),
      "No devices.",
    ),
    d.pins.length ? h("h3", null, "Routing pins") : null,
    d.pins.length
      ? table(
          [th("On"), th("Target"), th("Relays"), th("Note")],
          d.pins.map((p) => h("tr", null, td(p.target_kind), td(p.target_kind === "device" ? (names.get(p.target_id) ?? p.target_id) : "this account"), td(p.relays.join(", "), "mono"), td(p.note || "—"))),
          "",
        )
      : null,
    h("h2", null, "Usage per day and computer"),
    table(
      [th("Day", "num"), th("Computer"), th("To", "num"), th("From", "num"), th("Sessions", "num"), th("Friends", "num"), th("Session time", "num"), th("Online", "num")],
      hostUse.map((u) =>
        h("tr", null, td(u.day, "num"), td(names.get(u.device_id) ?? u.device_id), td(size(u.relay_out), "num"), td(size(u.relay_in), "num"), td(String(u.sessions), "num"), td(String(u.guest_sessions), "num"), td(hours(u.session_seconds), "num"), td(hours(u.online_seconds), "num")),
      ),
      "No usage in this period.",
    ),
    h("h2", null, "Shared windows"),
    table(
      [th("Window"), th("Mode"), th("Joined", "num"), th("Created", "num"), th("State")],
      d.shares.map((s) => {
        const live = !s.ended_at && s.expires_at > now;
        return h("tr", null, td(s.window_label || s.id), td(s.can_type ? "watch and type" : "watch only"), td(String(s.guests), "num"), td(dateTime(s.created_at), "num"), h("td", null, statusChip(live ? "active" : "ended")));
      }),
      "No shares.",
    ),
    h("h2", null, "Orders"),
    ordersTable(ord?.orders ?? []),
    h("h2", null, "API tokens"),
    table(
      [th("Name"), th("Hint"), th("Scope"), th("Created", "num"), th("Expires", "num"), th("Last used", "num"), th("State")],
      d.tokens.map((x) => h("tr", null, td(x.name || "—"), td(x.hint ? `…${x.hint}` : "—", "mono"), td(x.scope || "—"), td(date(x.created_at), "num"), td(x.expires_at ? date(x.expires_at) : "never", "num"), td(ago(x.last_used_at), "num"), h("td", null, statusChip(x.revoked_at ? "removed" : x.expires_at && x.expires_at < now ? "expired" : "active")))),
      "No API tokens.",
    ),
    h("h2", null, "Sign-ins from a terminal"),
    table(
      [th("Device"), th("Asked", "num"), th("Expires", "num"), th("State")],
      d.logins.map((x) => h("tr", null, td(x.name || "—"), td(dateTime(x.created_at), "num"), td(dateTime(x.expires_at), "num"), h("td", null, statusChip(x.collected ? "collected" : x.expires_at < now ? "expired" : "pending")))),
      "No sign-in requests.",
    ),
    h("h2", null, "Blocks ", link("/blocks", "all blocks →")),
    table(
      [th("Kind"), th("What"), th("Reason"), th("Since", "num"), th("State")],
      d.blocks.map((b) => h("tr", null, td(b.kind), td(b.kind === "device" ? (names.get(b.value) ?? b.value) : "this account"), td(b.reason || "—"), td(dateTime(b.created_at), "num"), h("td", null, statusChip(b.lifted_at ? "lifted" : b.expires_at && b.expires_at < now ? "expired" : "active")))),
      "Never blocked.",
    ),
    h("h2", null, "Recent events ", link(`/logs?account=${aid}`, "in the log →")),
    table(
      [th("When", "num"), th("Kind"), th("Level"), th("Message", "wrap"), th("Detail")],
      d.events.map((e) => h("tr", null, td(dateTime(e.t), "num"), td(e.kind, "mono"), h("td", null, statusChip(e.level)), td(e.message, "wrap"), h("td", null, detailBox(e.detail)))),
      "Nothing in the log about this account.",
    ),
  );
}

